import { Link } from "react-router-dom";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { MagnifyingGlass, Sparkle, PencilSimple, PaperPlaneTilt, ChartBar } from "@phosphor-icons/react";

const STEPS = [
  {
    icon: MagnifyingGlass,
    title: "Search a federal grant",
    body: "Open Search Grants and use keywords (e.g., 'rural broadband'). Filter by agency or status. Live data from Grants.gov.",
    cta: { to: "/search", label: "Search now" },
  },
  {
    icon: Sparkle,
    title: "Generate a proposal with AI",
    body: "Open a grant, describe your project in 2-3 sentences, click Generate Draft. Claude Sonnet 4.5 produces a 9-section proposal in ~30-60 seconds.",
    cta: { to: "/search", label: "Find grant" },
  },
  {
    icon: PencilSimple,
    title: "Edit & preview",
    body: "Tweak sections, replace bracketed placeholders ([$XXX,XXX]) with your actual numbers, save anytime. Toggle Edit/Preview tabs.",
    cta: { to: "/proposals", label: "My proposals" },
  },
  {
    icon: PaperPlaneTilt,
    title: "Submit",
    body: "Choose Simulated submission (PDF + reference#) or Email to agency contact (requires Resend API key). The status flips to Submitted.",
  },
  {
    icon: ChartBar,
    title: "Report the outcome",
    body: "Premium subscribers must report each submission's outcome (approved / declined / withdrawn) on the Reporting page within 14 days.",
    cta: { to: "/reporting", label: "Reporting" },
  },
];

const FAQ = [
  {
    q: "What does the $14.99 subscription include?",
    a: "A 30-day Premium pass: unlimited AI proposal drafts, outcome reporting dashboard, watermarked PDFs, agency-email submission, and the help assistant. Stack passes anytime.",
  },
  {
    q: "Is there a free trial?",
    a: "Yes — every new account gets a 7-day free trial automatically. No card required to start.",
  },
  {
    q: "Are my proposals stored privately?",
    a: "Yes. Each proposal is scoped to your user account. Admins can manage user accounts but never see proposal content.",
  },
  {
    q: "Why do you ask me to report outcomes?",
    a: "Aggregate outcome data lets us improve AI proposal quality and gives you a track record. Premium accounts must report within 14 days of submission to keep generating new drafts.",
  },
  {
    q: "How does payment security work?",
    a: "All card data is handled by Stripe Checkout. We only receive a payment session ID. We never see, store, or transmit your card number, CVC, or expiration.",
  },
  {
    q: "Can I get a refund?",
    a: "30-day passes are non-refundable once any AI generation has occurred during that window. Contact support@grantflow.it.com within 24 hours of purchase if no usage occurred.",
  },
  {
    q: "How do I email a proposal to a grants officer?",
    a: "Open the proposal → Submit → choose 'Email to agency contact' → enter the recipient. The proposal PDF is attached automatically. Sent through GrantFlow's verified email infrastructure.",
  },
];

export default function Help() {
  return (
    <div className="max-w-5xl">
      <div className="gf-overline">Help</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-2">
        Getting started.
      </h1>
      <p className="text-zinc-600 mt-3 max-w-2xl text-sm leading-relaxed">
        Five steps from a topic to a submitted proposal. Need quick help? Use the chat widget
        in the bottom-right — it's powered by Claude Sonnet 4.5 and trained on this app.
      </p>

      <div className="mt-10 grid md:grid-cols-2 gap-4">
        {STEPS.map((s, i) => (
          <div key={i} className="border border-zinc-200 bg-white p-6" data-testid={`help-step-${i}`}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-blue-700 flex items-center justify-center shrink-0">
                <s.icon size={20} color="white" weight="regular" />
              </div>
              <div>
                <div className="gf-overline">Step {i + 1}</div>
                <div className="font-display font-bold text-lg mt-1 tracking-tight">{s.title}</div>
                <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{s.body}</p>
                {s.cta && (
                  <Link to={s.cta.to} className="inline-block mt-3 text-blue-700 font-semibold hover:underline text-sm uppercase tracking-wide">
                    {s.cta.label} →
                  </Link>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-12 border border-zinc-200 bg-white p-6 md:p-8" data-testid="faq">
        <div className="gf-overline">FAQ</div>
        <h2 className="font-display font-bold text-2xl mt-2 tracking-tight">Frequently asked questions</h2>
        <Accordion type="single" collapsible className="mt-6">
          {FAQ.map((f, i) => (
            <AccordionItem key={i} value={`q-${i}`} className="border-zinc-200">
              <AccordionTrigger className="hover:no-underline text-left font-semibold text-zinc-900" data-testid={`faq-q-${i}`}>
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm text-zinc-600 leading-relaxed">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  );
}
